import { AxiosResponse } from 'axios'
import { http } from '@/core/realisations/Http.realisation'
import {
    BillingCalculateCostParams,
    BillingCalculateCostResponseT,
    BillingChangeAccountantPayload,
    BillingDiscountPayloadT,
    BillingGetAvrForPrintResponseT,
    BillingGetCalculationResponseT,
    BillingGetCalculationsParamsT,
    BillingGetCalculationsResponseT,
    BillingGetDiscountsResponseT,
    BillingGetIndividualCompaniesParamsT,
    BillingGetIndividualCompaniesResponseT,
    BillingGetInvoiceCalculationResponseT,
    BillingGetTariffByIdResponseT,
    BillingGetTariffResponseT,
    BillingGetTariffSecondByIdResponseT,
    BillingGetTariffThirdByIdResponseT,
    BillingGetTariffsResponseT,
    BillingGetTranscriptForPrintResponseT,
    BillingGetTranscriptResponseT,
    BillingGetTranscriptsParams,
    BillingGetTranscriptsResponseT,
    BillingGetTtnForPrintResponseT,
    BillingPreviewTranscriptParamsT,
    BillingPreviewTranscriptResponseT,
    CalculationPayloadT,
    CreateTariffPayloadT,
    IndividualTariffAdditionalServicesPayloadT,
    IndividualTariffParamsPayloadT,
    IndividualTariffPayloadT,
    TariffPayloadT,
    UpdateCalculationPayload,
    UpdateTranscriptPayload,
    ManualGenerateTranscriptPayload,
    BillingGetAdditionalServicesPricesParams,
    BillingGetAdditionalServicesPricesResponse,
    BillingUpdateCalculationPricePayload,
    BillingGetCompanyResponseT,
    GetAdditionalServicesTariffsParams,
    InternationalCalculationPayloadT,
    SetCostResponse,
    BillingSetVatPayload,
    GetFtlCalculatorsPayload,
    GetFtlCalculatorsResponse,
    GetFtlTariffsParams,
    GetFtlTariffsResponse,
    GetShipmentCostsFtlParams,
    GetShipmentCostsFtlResponse,
    BillingCalculateFtlCostParams,
    BillingCalculateCostFtlResponseT,
    FtlTariffPayload,
    BillingGetFtlTariffByIdResponse,
    GetStandard2025TariffsResponse,
    GetMinWeightResponse,
} from '../types/billing.types'
import { HistoryInvoiceBillingT } from '@/core/types/invoices.types'

export class BillingRepository {
    getCalculations(
        params: BillingGetCalculationsParamsT
    ): Promise<AxiosResponse<BillingGetCalculationsResponseT>> {
        return http.get('/billing/api/calculations', { params })
    }

    previewTranscript(
        params: BillingPreviewTranscriptParamsT
    ): Promise<AxiosResponse<BillingPreviewTranscriptResponseT>> {
        return http.get('/billing/api/calculations/preview/transcript', {
            responseType: 'blob',
            params,
        })
    }

    getTariffs(page: number): Promise<AxiosResponse<BillingGetTariffsResponseT>> {
        const params = {
            page,
        }

        return http.get('/billing/api/shipment-costs', { params })
    }

    getTariffsStandard(): Promise<AxiosResponse<BillingGetTariffsResponseT>> {
        const params = {
            companyId: 0,
            limit: 9999,
        }

        return http.get('/billing/api/shipment-costs', { params })
    }

    getCompaniesIndividual(
        payload: BillingGetIndividualCompaniesParamsT
    ): Promise<AxiosResponse<BillingGetIndividualCompaniesResponseT>> {
        const params = {
            ...payload,
            limit: 50,
            shipmentTariffId: 2,
        }

        return http.get('/cabinet/api/v2/companies', { params })
    }

    getCompaniesFtl(
        params: BillingGetIndividualCompaniesParamsT
    ): Promise<AxiosResponse<BillingGetIndividualCompaniesResponseT>> {
        return http.get('/billing/api/shipment-costs-ftl/companies', {
            params: { ...params, limit: 50 },
        })
    }

    getCompaniesUnique(
        payload: BillingGetIndividualCompaniesParamsT
    ): Promise<AxiosResponse<BillingGetIndividualCompaniesResponseT>> {
        const params = {
            ...payload,
            limit: 50,
            shipmentTariffId: 3,
        }

        return http.get('/cabinet/api/v2/companies', { params })
    }

    getInvoiceCalculation(
        invoice_id: number
    ): Promise<AxiosResponse<BillingGetInvoiceCalculationResponseT>> {
        return http.get(`/billing/api/calculations/${invoice_id}/invoice`)
    }

    getTranscriptForPrint(
        invoice_number: string
    ): Promise<AxiosResponse<BillingGetTranscriptForPrintResponseT>> {
        return http.get(`/billing/api/transcripts/${invoice_number}/invoice`)
    }

    getAvrForPrint(invoice_number: string): Promise<AxiosResponse<BillingGetAvrForPrintResponseT>> {
        return http.get(`/cabinet/api/avr/${invoice_number}`)
    }

    getCalculation(calculation_id: number): Promise<AxiosResponse<BillingGetCalculationResponseT>> {
        return http.get(`/billing/api/calculations/${calculation_id}`)
    }

    getTariff(tariff_id: number): Promise<AxiosResponse<BillingGetTariffResponseT>> {
        return http.get(`/billing/api/shipment-costs/${tariff_id}`)
    }

    getTariffByCompanyId(companyId: number): Promise<AxiosResponse<BillingGetTariffByIdResponseT>> {
        const params = { companyId, limit: 9999 }
        return http.get('/billing/api/shipment-costs', { params })
    }

    getFtlTariffsByCompanyId(
        companyId: number
    ): Promise<AxiosResponse<BillingGetFtlTariffByIdResponse>> {
        const params = { companyId, limit: 9999 }
        return http.get('/billing/api/shipment-costs-ftl', { params })
    }

    getTariffSecondByCompanyId(
        companyId: number
    ): Promise<AxiosResponse<BillingGetTariffSecondByIdResponseT>> {
        const params = { companyId, limit: 9999 }
        return http.get('/billing/api/standard-params', { params })
    }

    getTariffThirdByCompanyId(
        params: GetAdditionalServicesTariffsParams
    ): Promise<AxiosResponse<BillingGetTariffThirdByIdResponseT>> {
        return http.get('/billing/api/additional-services', { params })
    }

    createCalculation(payload: CalculationPayloadT): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/calculations', payload)
    }

    setCost(id: number): Promise<AxiosResponse<SetCostResponse>> {
        return http.post(`/billing/api/calculations/${id}/set-paid`, { id })
    }

    createInternationalCalculation(
        payload: InternationalCalculationPayloadT
    ): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/calculations/international', payload)
    }

    addIndividualTariff(payload: IndividualTariffPayloadT): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/shipment-costs/individual', payload)
    }

    addFtlTariff(payload: FtlTariffPayload): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/shipment-costs-ftl/individual', payload)
    }

    editIndividualTariff(payload: IndividualTariffPayloadT): Promise<AxiosResponse<void>> {
        return http.put('/billing/api/shipment-costs/individual/update', payload)
    }

    getMinWeight(id: number): Promise<AxiosResponse<GetMinWeightResponse>> {
        return http.get(`/billing/api/standard-params/weight/${id}`, { params: { id } })
    }

    addIndividualTariffParams(
        payload: IndividualTariffParamsPayloadT
    ): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/standard-params/individual', payload)
    }

    editIndividualTariffParams(
        payload: IndividualTariffParamsPayloadT
    ): Promise<AxiosResponse<void>> {
        return http.put('/billing/api/standard-params/individual/update', payload)
    }

    addAdditionalServices(
        payload: IndividualTariffAdditionalServicesPayloadT
    ): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/additional-services/individual', payload)
    }

    editAdditionalServices(
        payload: IndividualTariffAdditionalServicesPayloadT
    ): Promise<AxiosResponse<void>> {
        return http.put('/billing/api/additional-services/individual/update', payload)
    }

    handleBilling(params: HistoryInvoiceBillingT): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/admin/invoices/${params.invoice_id}/billing`, {
            companyId: params.companyId || null,
        })
    }

    getTtnForPrint(invoice_number: string): Promise<AxiosResponse<BillingGetTtnForPrintResponseT>> {
        return http.get(`/cabinet/api/admin/invoices/${invoice_number}/ttn`)
    }

    updateTranscript(
        transcript_id: number,
        payload: UpdateTranscriptPayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/billing/api/transcripts/${transcript_id}`, payload)
    }

    updateCalculation(
        calculation_id: number,
        payload: UpdateCalculationPayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/billing/api/calculations/${calculation_id}`, payload)
    }

    updateTariff(tariff_id: number, payload: TariffPayloadT): Promise<AxiosResponse<void>> {
        return http.put(`/billing/api/shipment-costs/${tariff_id}`, payload)
    }

    getTranscripts(
        params: BillingGetTranscriptsParams
    ): Promise<AxiosResponse<BillingGetTranscriptsResponseT>> {
        return http.get('/billing/api/transcripts', { params })
    }

    getTranscript(transcript_id: number): Promise<AxiosResponse<BillingGetTranscriptResponseT>> {
        return http.get(`/billing/api/transcripts/${transcript_id}`)
    }

    downloadFile(file_id: number): Promise<AxiosResponse<any>> {
        return http.get(`/billing/api/files/${file_id}`, { responseType: 'blob' })
    }

    download1CFile(transcript_id: number): Promise<AxiosResponse<any>> {
        return http.get(`/billing/api/files/${transcript_id}/transcript-onec-files`, {
            responseType: 'blob',
        })
    }

    generateCompanyTranscript(company_id: number): Promise<AxiosResponse<void>> {
        return http.post(`/billing/api/transcripts/${company_id}/generate`, {})
    }

    exportTranscripts(start_date: string, end_date: string): Promise<AxiosResponse<any>> {
        const params = {
            startDate: start_date,
            endDate: end_date,
        }

        return http.get('/billing/api/transcripts/export', { params, responseType: 'blob' })
    }

    createTariff(payload: CreateTariffPayloadT): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/shipment-costs', payload)
    }

    getDiscounts(page: number): Promise<AxiosResponse<BillingGetDiscountsResponseT>> {
        const params = {
            page,
        }

        return http.get('/billing/api/discounts', { params })
    }

    createDiscount(payload: BillingDiscountPayloadT): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/discounts', payload)
    }

    printWaybill(invoice_id: number): Promise<AxiosResponse<any>> {
        return http.get(`/print/api/waybill/${invoice_id}`, { responseType: 'blob' })
    }

    applyAccountantToCompany(
        company_id: number,
        accountant_id: number
    ): Promise<AxiosResponse<void>> {
        return http.put(`/billing/api/companies/${company_id}/accountant/${accountant_id}`, {})
    }

    getCompany(company_id: number): Promise<AxiosResponse<BillingGetCompanyResponseT>> {
        return http.get(`/billing/api/companies/${company_id}`)
    }

    changeAccountant(payload: BillingChangeAccountantPayload): Promise<AxiosResponse<void>> {
        return http.put('/billing/api/companies/assign-accountant', payload)
    }

    calculateCost(
        payload: BillingCalculateCostParams
    ): Promise<AxiosResponse<BillingCalculateCostResponseT>> {
        return http.post('/billing/api/v2/calculators', payload)
    }

    calculateCostV3(
        payload: BillingCalculateCostParams
    ): Promise<AxiosResponse<BillingCalculateCostResponseT>> {
        return http.post('/billing/api/v3/calculators', payload)
    }

    calculateCostFtl(
        payload: BillingCalculateFtlCostParams
    ): Promise<AxiosResponse<BillingCalculateCostFtlResponseT>> {
        return http.post('/billing/api/v2/ftl/calculators', payload)
    }

    deleteTranscript(transcript_id: number): Promise<AxiosResponse<void>> {
        return http.delete(`/billing/api/transcripts/${transcript_id}`)
    }

    manualGenerateTranscript(
        payload: ManualGenerateTranscriptPayload
    ): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/transcripts', payload)
    }

    getFtlCalculators(
        payload: GetFtlCalculatorsPayload
    ): Promise<AxiosResponse<GetFtlCalculatorsResponse>> {
        return http.post('/billing/api/v2/ftl/calculators', payload)
    }

    getShipmentCostsFtl(
        params: GetShipmentCostsFtlParams
    ): Promise<AxiosResponse<GetShipmentCostsFtlResponse>> {
        return http.get('/billing/api/shipment-costs-ftl', { params })
    }

    getFtlTariffs(params: GetFtlTariffsParams): Promise<AxiosResponse<GetFtlTariffsResponse>> {
        return http.get('/billing/api/additional-services', { params })
    }

    updateFewCalculations(calculationIds: number[]): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/calculations/update-few-calculations', { calculationIds })
    }

    getAdditionalServicesPrices(
        params: BillingGetAdditionalServicesPricesParams
    ): Promise<AxiosResponse<BillingGetAdditionalServicesPricesResponse>> {
        return http.get('/billing/api/additional-service-values', { params })
    }

    updateCalculationPrice(
        calculation_id: number,
        payload: BillingUpdateCalculationPricePayload
    ): Promise<AxiosResponse<void>> {
        return http.put(`/billing/api/calculations/${calculation_id}/cost`, payload)
    }

    setVat(company_id: number, payload: BillingSetVatPayload): Promise<AxiosResponse<void>> {
        return http.put(`/billing/api/companies/${company_id}/billing-settings`, payload)
    }

    importInvoices(form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData('/cabinet/api/admin/invoices/import-send-to-billing', form_data)
    }

    getStandard2025Tariffs(zoneId: number): Promise<AxiosResponse<GetStandard2025TariffsResponse>> {
        return http.get(`/billing/api/default-tariffs/${zoneId}`)
    }

    createDefaultTariffV3(payload: {
        weight: number
        companyId: number
    }): Promise<AxiosResponse<void>> {
        return http.post('/billing/api/v3/shipment-costs/default', payload)
    }
}
